/**
 * Remise à l'endroit des photos avant leur insertion dans le DWG.
 *
 * Deux causes de photo « à l'envers » sur tablette :
 * 1. Fichier JPEG portant une orientation EXIF (appareil photo du système,
 *    galerie) : le navigateur l'applique à l'affichage, ODA et AutoCAD
 *    l'ignorent. On « cuit » donc l'orientation dans les pixels.
 * 2. Capture dans l'app (getUserMedia) : l'image suit l'orientation de
 *    l'ÉCRAN, pas celle de la tablette. Rotation automatique verrouillée (ou
 *    écran resté dans l'ancien sens), la photo sort pivotée. On compare
 *    l'orientation physique (gravité, `deviceorientation`) à celle de l'écran
 *    et on corrige à la capture.
 */

/** Rotation en quarts de tour, en degrés. */
export type QuarterTurn = 0 | 90 | 180 | 270;

/**
 * En dessous de cette inclinaison (sinus de l'angle avec l'horizontale) la
 * tablette est tenue à plat — photo du sol, d'une sous-face : le « haut » n'a
 * plus de sens physique, on garde le sens de l'écran.
 */
const MIN_TILT = 0.5; // ≈ 30°

// ============== EXIF ==============

/**
 * Orientation EXIF (1 à 8) d'un JPEG, 1 si absente ou illisible. Lecture
 * directe du segment APP1, sans dépendance.
 */
export function readJpegOrientation(bytes: Uint8Array): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return 1;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset);
    // Début des données d'image : plus de métadonnées après.
    if (marker === 0xffda || (marker & 0xff00) !== 0xff00) return 1;
    const length = view.getUint16(offset + 2);
    if (marker === 0xffe1) {
      return readExifOrientation(view, offset + 4, length - 2);
    }
    offset += 2 + length;
  }
  return 1;
}

function readExifOrientation(
  view: DataView,
  start: number,
  length: number,
): number {
  const end = start + length;
  // "Exif\0\0"
  if (end > view.byteLength || view.getUint32(start) !== 0x45786966) return 1;
  const tiff = start + 6;
  if (tiff + 8 > end) return 1;
  const little = view.getUint16(tiff) === 0x4949;
  const ifd0 = tiff + view.getUint32(tiff + 4, little);
  if (ifd0 + 2 > end) return 1;
  const entries = view.getUint16(ifd0, little);
  for (let i = 0; i < entries; i++) {
    const entry = ifd0 + 2 + i * 12;
    if (entry + 12 > end) return 1;
    if (view.getUint16(entry, little) === 0x0112) {
      const value = view.getUint16(entry + 8, little);
      return value >= 1 && value <= 8 ? value : 1;
    }
  }
  return 1;
}

/**
 * JPEG dont l'orientation EXIF n'est pas « normale » : réencodé avec
 * l'orientation appliquée aux pixels (le navigateur la respecte au décodage).
 * Sinon, octets d'origine inchangés — pas de recompression inutile.
 */
export async function normalizeImageOrientation(
  bytes: Uint8Array,
  mimeType: string,
): Promise<{ bytes: Uint8Array; changed: boolean }> {
  if (!/jpe?g/i.test(mimeType) || readJpegOrientation(bytes) === 1) {
    return { bytes, changed: false };
  }
  try {
    const bitmap = await createImageBitmap(
      new Blob([bytes as Uint8Array<ArrayBuffer>], { type: mimeType }),
      { imageOrientation: "from-image" },
    );
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob = await canvasToJpeg(canvas);
    return { bytes: new Uint8Array(await blob.arrayBuffer()), changed: true };
  } catch (error) {
    console.warn("[imageOrientation] EXIF normalization failed:", error);
    return { bytes, changed: false };
  }
}

// ============== Capture caméra ==============

/**
 * Rotation physique de la tablette par rapport à son orientation naturelle,
 * dans la convention de `screen.orientation.angle` (tablette tournée de θ dans
 * le sens antihoraire ⇒ θ), d'après `beta`/`gamma` de `deviceorientation`.
 * Null si la tablette est trop à plat pour trancher.
 *
 * Le « haut » du monde vu dans le repère de l'appareil (x à droite, y vers le
 * haut de l'écran) vaut (−cos β · sin γ, sin β) — 3e ligne de la matrice
 * Rz(α)·Rx(β)·Ry(γ) de la spec Device Orientation.
 */
export function physicalRotation(
  beta: number,
  gamma: number,
): QuarterTurn | null {
  const b = (beta * Math.PI) / 180;
  const g = (gamma * Math.PI) / 180;
  const upX = -Math.cos(b) * Math.sin(g);
  const upY = Math.sin(b);
  if (Math.hypot(upX, upY) < MIN_TILT) return null;
  const degrees = (Math.atan2(upX, upY) * 180) / Math.PI;
  return snapQuarter(degrees);
}

/**
 * Quart de tour à appliquer (sens antihoraire) à une image capturée dans le
 * sens de l'écran pour la remettre dans le sens du monde.
 */
export function captureCorrection(
  physical: QuarterTurn | null,
  screenAngle: number,
): QuarterTurn {
  if (physical === null) return 0;
  return snapQuarter(physical - screenAngle);
}

function snapQuarter(degrees: number): QuarterTurn {
  const quarter = Math.round(degrees / 90);
  return ((((quarter % 4) + 4) % 4) * 90) as QuarterTurn;
}

/**
 * Dessine `source` (w × h) dans `canvas` tourné de `angle` dans le sens
 * antihoraire. Le canvas prend les dimensions de l'image tournée.
 */
export function drawRotated(
  canvas: HTMLCanvasElement,
  source: CanvasImageSource,
  width: number,
  height: number,
  angle: QuarterTurn,
): void {
  const swap = angle === 90 || angle === 270;
  canvas.width = swap ? height : width;
  canvas.height = swap ? width : height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  // Canvas : y vers le bas, un angle positif tourne en sens horaire.
  ctx.rotate((-angle * Math.PI) / 180);
  ctx.drawImage(source, -width / 2, -height / 2);
  ctx.restore();
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
      "image/jpeg",
      0.92,
    );
  });
}
